import React, { useEffect, useState } from "react";
import { Menu, X, Instagram, Facebook } from "lucide-react";
import TikTokIcon from "./icons/TikTokIcon";

const LINKS = [
  { href: "#home", label: "Home" },
  { href: "#about", label: "About Author" },
  { href: "#book", label: "Books" },
  { href: "#newsletter", label: "Newsletter" },
  {
    href: "https://www.goodreads.com/author/show/57797883.Chelsea_Ray",
    label: "Goodreads",
    external: true,
  },
  { href: "#socials", label: "Socials" },
  { href: "#contact", label: "Contact" },
];

const NAV_SOCIALS = [
  {
    id: "instagram",
    href: "https://www.instagram.com/chelsearaywrites",
    icon: Instagram,
    aria: "Follow Chelsea Ray on Instagram",
    testId: "nav-social-instagram",
  },
  {
    id: "tiktok",
    href: "https://www.tiktok.com/@chelsearaywrites",
    icon: TikTokIcon,
    aria: "Follow Chelsea Ray on TikTok",
    testId: "nav-social-tiktok",
  },
  {
    id: "facebook",
    href: "https://www.facebook.com/profile.php?id=61578028846197",
    icon: Facebook,
    aria: "Follow Chelsea Ray on Facebook",
    testId: "nav-social-facebook",
  },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid="nav-bar"
      aria-label="Primary"
      className={`fixed top-0 inset-x-0 z-50 border-b border-transparent transition-all duration-500 ${
        scrolled ? "nav-scrolled" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-6">
        <a
          href="#home"
          data-testid="nav-logo"
          className="font-display text-xl md:text-2xl text-[#f2e8d5] tracking-wide italic shrink-0"
          aria-label="Against All Waves, home"
        >
          <span className="text-[#c9a25a]">A</span>gainst{" "}
          <span className="text-[#c9a25a]">A</span>ll{" "}
          <span className="text-[#c9a25a]">W</span>aves
        </a>

        <div className="hidden md:flex items-center gap-5 lg:gap-8">
          <ul className="flex items-center gap-5 lg:gap-7">
            {LINKS.map((l) => (
              <li key={l.href}>
                <a
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  href={l.href}
                  {...(l.external
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                  className="font-accent text-[11px] text-[#a8b4c0] hover:text-[#e8c888] transition-colors duration-500 relative group whitespace-nowrap"
                >
                  {l.label}
                  <span className="absolute -bottom-2 left-0 right-0 h-px bg-[#c9a25a] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </a>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3 pl-4 lg:pl-5 border-l border-[#c9a25a]/20">
            {NAV_SOCIALS.map(({ id, href, icon: Icon, aria, testId }) => (
              <a
                key={id}
                data-testid={testId}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={aria}
                className="text-[#c9a25a]/80 hover:text-[#f3dca0] transition-colors duration-500 p-1"
              >
                <Icon size={15} aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-[#f2e8d5] p-2"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <div
        data-testid="nav-mobile-panel"
        className={`md:hidden overflow-hidden transition-[max-height] duration-500 ease-in-out ${
          open ? "max-h-[520px]" : "max-h-0"
        }`}
      >
        <ul className="px-6 pb-4 pt-2 flex flex-col gap-5 bg-[#06090e]/95 backdrop-blur-md border-t border-[#c9a25a]/15">
          {LINKS.map((l) => (
            <li key={l.href}>
              <a
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                href={l.href}
                {...(l.external
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
                onClick={() => setOpen(false)}
                className="block font-accent text-xs text-[#a8b4c0] hover:text-[#e8c888] tracking-[0.22em]"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>
        <div className="px-6 pb-6 flex items-center gap-5 bg-[#06090e]/95 backdrop-blur-md border-t border-[#c9a25a]/10 pt-4">
          {NAV_SOCIALS.map(({ id, href, icon: Icon, aria, testId }) => (
            <a
              key={id}
              data-testid={`${testId}-mobile`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={aria}
              onClick={() => setOpen(false)}
              className="text-[#c9a25a]/85 hover:text-[#f3dca0] p-1"
            >
              <Icon size={18} aria-hidden="true" />
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
}
